import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AbstractItemComponent } from '@df/catalog-ui/abstract-item.component';
import { Listing } from '@df/catalog-ui/listing/listing';
import { IPageDataV2, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import orderBy from 'lodash-es/orderBy';
import { BreadcrumbsComponent } from 'projects/app-default/src/app/catalog/meta/breadcrumbs.component';
import { LocalPage } from 'projects/app-default/src/app/catalog/page/local/local-page';
import { ButtonStyleComponent } from 'projects/app-default/src/app/common/buttons/button-style/button-style.component';
import { Dropdown2Component } from 'projects/app-default/src/app/common/dropdown2/dropdown2.component';
import { Subscription } from 'rxjs';
import { NBlogCategoryPageType } from '../../page/category-view/cms-blog-page-category-view.component';
import { CmsBlogContentBlogCardComponent } from '../blog-card/cms-blog-content-blog-card.component';
import { CmsBlogContentTagFilter } from '../tag-filter/cms-blog-content-tag-filter';
import { NBlogPostPageType } from '../../page/post-view/cms-blog-post.namespace';
import { NBlogRootPageType } from '../../page/root-view/cms-blog-root-page.namespace';

interface INavigationItem {
    url: string;
    name: string;
    id?: number;
}

@Component({
    standalone: true,
    imports: [
        CommonModule,
        Dropdown2Component,
        GridModule,
        CmsBlogContentBlogCardComponent,
        ButtonStyleComponent,
        TranslationModule,
        RouterModule,
        BreadcrumbsComponent
    ],
    selector: 'cms-blog-content-post-listing',
    templateUrl: './cms-blog-content-post-listing.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsBlogContentPostListingViewComponent extends AbstractItemComponent<LocalPage<IPageDataV3<NBlogCategoryPageType.ITypeData>>> {
    type = ICmsPage.type;

    readonly isNavOpen = signal(false);

    /**
     * Listing subscfiptions
     */
    private listingSubscription?: Subscription;

    /**
     * Children posts listing
     */
    listing!: Listing<LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>>;

    /**
     * Blog page origin
     */
    blogOrigin: string = NBlogCategoryPageType.blogOrigin;

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        private translationService: TranslationService
    ) {
        super(changeDetectorRef, app, timeoutService);
    }
    /**
     * Type data getter shorthand
     */
    get typeData() {
        return this.model?.data?.typeData;
    }

    /**
     * Parent page type data shorthand
     */
    private get parentTypeData() {
        return (this.model?.parent?.data as IPageDataV3<NBlogCategoryPageType.ITypeData>)?.typeData;
    }
    /**
     * Sibling categories
     */
    private siblingsOrChildren: LocalPage<IPageDataV2>[] = [];

    /**
     * Setup listing on model updates
     */

    protected override onModelChange(): void {
        this.blogOrigin = this.getBlogOrigin();
        super.onModelChange();
        this.listingSubscription?.unsubscribe();
        this.listing?.clearSubscriptions();
        this.listing = new Listing<LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>>(
            {
                type: 'page',
                storage: true,
                infScrollInit: [12, 16, 16, 16],
                infScrollStep: [12, 16, 16, 16]
            },
            orderBy(
                this.model?.getBlogCategoryPosts() as LocalPage<IPageDataV3<NBlogPostPageType.ITypeData>>[],
                i => (!i.data?.typeData?.date ? 0 : new Date(i.data?.typeData?.date)?.getTime()) || [],
                'desc'
            )
        );
        this.listing.addFilter(new CmsBlogContentTagFilter({ id: 'tags', isOpen: true, position: 0, type: 'custom' }, this.listing));
        this.listing.applyFilters();
        const parentId =
            this.model?.data?.pageType === NBlogRootPageType.typeId ? this.model?.id : this.model?.data?.parent || this.model?.id;
        this.siblingsOrChildren = this.app.resources.page
            .getAllExisting()
            .filter(i => i.data?.pageType === NBlogCategoryPageType.typeId && i.data?.parent === parentId) as LocalPage<IPageDataV2>[];
        this.listing.subscribe(() => this.detectChanges());
    }

    get navigationItems(): INavigationItem[] {
        return [
            { name: this.translationService.translate('All articles'), url: this.blogOrigin },
            ...(this.typeData?.tagRedirect ? this.siblingsOrChildren : this.tagFilter.options)
                .map(i => ({
                    name: i.name,
                    url: i.data?.url || '',
                    id: i.id,
                    position: i.data?.position || 0
                }))
                .sort((a, b) => b.position - a.position)
        ];
    }

    get currentCategoryName(): string {
        const item = this.navigationItems.find(item => this.isSelected(item));
        return (item?.id && item?.name) || this.translationService.translate('All articles');
    }

    get tagFilter() {
        return this.listing.filtersMap['tags'] as CmsBlogContentTagFilter;
    }

    get name() {
        return this.typeData?.useParent ? this.model?.parent?.data?.name : this.model?.data?.name;
    }

    get topCopy() {
        return this.typeData?.useParent ? this.parentTypeData?.topCopy : this.typeData?.topCopy;
    }

    get bottomCopy() {
        return this.typeData?.useParent ? this.parentTypeData?.bottomCopy : this.typeData?.bottomCopy;
    }

    /**
     * No option passed for 'view all'
     */
    isSelected(option: INavigationItem): boolean {
        if (this.typeData?.tagRedirect) {
            return option.id ? option.id === this.model?.id : this.model?.data?.url === this.blogOrigin;
        }
        return option.id ? this.tagFilter.isSelected(option.id) : !this.tagFilter.hasSelection;
    }

    /**
     * No option passed for 'view all'
     */
    selectNav(option: INavigationItem): void {
        if (!this.typeData?.tagRedirect) {
            if (option.id) {
                this.tagFilter.select(option.id);
            } else {
                this.tagFilter.reset();
            }
        }
    }

    openChangeHandler(event: boolean): void {
        this.isNavOpen.set(event);
        this.detectChanges();
    }

    private getBlogOrigin(): string {
        return (
            [this.model, this.model?.parent, this.model?.parent?.parent].find(model => model?.data?.pageType === NBlogRootPageType.typeId)
                ?.data?.url || NBlogCategoryPageType.blogOrigin
        );
    }
}
